import { useState, useCallback, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import useIsMobile from "../shared/Mobile-Responsive";
import { ArrowLeft, X, Upload, Share2 } from 'lucide-react';
import { getStatusColor, getStatusText, getRiskStatusBadge, getRiskText, getEffectiveStatus, getModeIcon, formatTime, calculateDuration, formatSymptomLevel } from "../../../../utils/index";
import SessionHistory from "./SessionHistory";
import { getAppointmentCounter, getAppointmentDetailData } from "../../api/Appointments/AppointmentDetail";


const AppointmentDetails = ({ onBack, onClose, onShare, onExport, onSessionUpdated }) => {
  const [status, setStatus] = useState("active");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [detailData, setDetailData] = useState({});
  const [myAppointment, setMyAppointment] = useState([]);

  const { appointmentId } = useParams();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  // Redux state
  const { data: appointmentCounterData, loading: appointmentCounterLoading } = useSelector(
    (state) => state.appointmentDetail?.appointmentCounter || {}
  );
  const { detail, loading: detailLoading, error: detailError } = useSelector(
    (state) => state.appointmentDetail?.appointmentDetail || {}
  );


  // API calls - Stable references to prevent useEffect re-runs
  const getAppointmentData = useCallback(() => {
    const payload = JSON.stringify({});
    dispatch(getAppointmentCounter(payload));
  }, [dispatch]);

  const getBasicInfo = useCallback((id) => {
    dispatch(getAppointmentDetailData(id));
  }, [dispatch]);

  const filterAllData = useCallback(() => {
    // Check if we have appointment counter data
    if (!appointmentCounterData) {
      return;
    }

    // Extract appointments from different possible data structures
    let allAppointments = [];

    if (Array.isArray(appointmentCounterData)) {
      allAppointments = appointmentCounterData;
    } else if (typeof appointmentCounterData === 'object') {
      // Handle nested response structure
      if (appointmentCounterData.payload) {
        allAppointments = Object.values(appointmentCounterData.payload).flat().filter(Boolean);
      } else if (appointmentCounterData.data) {
        allAppointments = Object.values(appointmentCounterData.data).flat().filter(Boolean);
      } else {
        // Try to extract from direct object values
        allAppointments = Object.values(appointmentCounterData).flat().filter(Boolean);
      }
    }

    if (allAppointments.length > 0) {
      // Find the current appointment details
      let appointmentDetail = allAppointments.find(
        (item) => item.appointmentId === appointmentId
      );

      if (appointmentDetail) {
        setDetailData(appointmentDetail);

        // Get all appointments for this user (for session history)
        let userAppointments = allAppointments.filter(
          (item) => item.uid === appointmentDetail.uid
        );

        // Process session history data using utility functions
        const processedSessions = userAppointments.map((session, index) => {
          // Process prescriptions by type
          const prescriptionsByType = (session.prescriptions || []).reduce((acc, prescription) => {
            acc[prescription.prescriptionType] = prescription;
            return acc;
          }, {});

          return {
            ...session,
            // Use utility function for session findings
            sessionFindings: {
              selfHarmSymptoms: formatSymptomLevel(session.selfHarmSymptoms),
              depressionSymptoms: formatSymptomLevel(session.depressionSymptoms),
              anxietySymptoms: formatSymptomLevel(session.anxietySymptoms),
              angerIssues: formatSymptomLevel(session.angerIssues)
            },
            // Use real API data for recommendations
            recommendations: {
              assessments: prescriptionsByType.ASSESSMENT ? {
                name: prescriptionsByType.ASSESSMENT.fileName || "Assessment",
                url: prescriptionsByType.ASSESSMENT.url
              } : null,
              exercises: prescriptionsByType.EXERCISE ? {
                name: prescriptionsByType.EXERCISE.fileName || "Exercise",
                url: prescriptionsByType.EXERCISE.url
              } : null,
              clinicalConsultation: session.clinicalConsultation ? "Yes" : "No",
              medicalConsultation: session.medicalConsultation ? "Yes" : "No"
            }
          };
        })
        // Sort by date (most recent first)
        .sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateB.getTime() - dateA.getTime();
        });

        setMyAppointment(processedSessions);
      }
    }
  }, [appointmentCounterData, detail, appointmentId]);

  // Effects - Optimized to prevent duplicate API calls
  useEffect(() => {
    if (appointmentId) {
      getAppointmentData();
      getBasicInfo(appointmentId);
    }
  }, [appointmentId]); // Removed function dependencies to prevent re-calls

  useEffect(() => {
    // Call filterAllData even if we only have appointmentCounterData
    // The detail data is used separately for member info
    if (appointmentCounterData) {
      filterAllData();
    }
  }, [appointmentCounterData, detail]); // Removed filterAllData dependency to prevent re-calls




  // Memoized appointment data from props
  const appointmentDetails = useMemo(() => {
    // Extract detail data from various possible structures
    let memberDetail = null;
    if (detail) {
      if (detail.payload) {
        memberDetail = detail.payload;
      } else if (detail.data) {
        memberDetail = detail.data;
      } else if (typeof detail === 'object' && detail.firstName) {
        memberDetail = detail;
      }
    }

    // Map API data to component structure
    return {
      appointmentId: detailData?.appointmentId || appointmentId || "N/A",
      type: detailData?.appointmentType || "Follow-up",
      date: detailData?.date || "N/A",
      timeSlot: detailData?.startTime && detailData?.endTime
        ? `${formatTime(detailData.startTime)}–${formatTime(detailData.endTime)}`
        : "N/A",
      status: getStatusText(detailData?.status, detailData?.currentStatus) || "N/A",
      statusColor: getStatusColor(detailData?.status, detailData?.currentStatus),
      mode: detailData?.mode || "N/A",
      practitioner: detailData ? `${detailData.counsellorFirstName || ''} ${detailData.counsellorLastName || ''}`.trim() :
                    memberDetail ? `${memberDetail.counsellorFirstName || ''} ${memberDetail.counsellorLastName || ''}`.trim() :
                    "N/A",
      practitionerSpecialization: detailData?.specialization || memberDetail?.specialization || "Clinical Psychologist",
      memberName: memberDetail ? `${memberDetail.firstName || ''} ${memberDetail.lastName || ''}`.trim() : "N/A",
      memberEmail: memberDetail?.email || "N/A",
      memberPhone: memberDetail?.mobile ? `${memberDetail.countryCode || '+91'}-${memberDetail.mobile}` : "N/A",
      memberGender: memberDetail?.gender || "N/A",
      memberDOB: memberDetail?.dateOfBirth || "N/A",
      memberGroups: memberDetail?.organisationName || "N/A",
      risk: getRiskText(detailData?.riskStatus),
      riskStatusRaw: detailData?.riskStatus,
      appointmentCreatedBy: detailData?.isFollowup ? "Counsellor" : "Member",
      duration: calculateDuration(detailData?.startTime, detailData?.endTime),
      memberJoined: detailData?.studentJoined || "N/A",
      practitionerJoined: detailData?.counsellorJoined || "N/A",
      sessionEnded: detailData?.status === "COMPLETED" ? "Yes" : "No",
      riskRatingSubmitted: (detailData?.riskStatus !== null && detailData?.riskStatus !== undefined) ? "Yes" : "No",
      notes: detailData?.notesText || memberDetail?.notes || "N/A",
      prescriptions: detailData?.prescriptions?.length > 0 ? "Available" : "N/A",
      createdOn: detailData?.createdOn || "N/A",
      updatedOn: detailData?.updatedOn || "N/A"
    };
  }, [detailData, detail, appointmentId, formatTime, calculateDuration]);


  const handleEmailShare = useCallback(() => {
    handleSaveFile();
    const subject = encodeURIComponent("Appointment Details");
    const body = encodeURIComponent(
      "Hi,\n\nPlease find the attached appointment details file.\n\nThanks!"
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  }, []);

  const handlePDFExport = useCallback(() => {
    // Open member summary report in new tab with appointment parameters
    const aid = appointmentDetails.appointmentId || appointmentId;
    // Get UID from detailData or detail
    const uid = detailData?.uid || detail?.payload?.pcUserId || detail?.data?.pcUserId || detail?.pcUserId;
    if (uid) {
      window.open(`/member-summary-report/${aid}?uid=${uid}`, '_blank');
    } else {
      window.open(`/member-summary-report/${aid}`, '_blank');
    }
  }, [appointmentDetails.appointmentId, appointmentId, detailData, detail]);

  const handleSaveFile = useCallback(() => {
    handlePDFExport();
  }, [handlePDFExport]);

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen(prev => !prev);
  }, []);


  return (
    <>
      {isMobile ? (
        <>
          <div className="font-poppins bg-[#F8FAFC] min-h-screen mx-auto">
            {/* Header */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-b-[1px] border-solid border-x-0 border-t-0 border-[#F1F5F9]">
              <div className="flex items-center gap-3">
                <button onClick={onBack} className="flex items-center justify-center bg-transparent border-0 p-0 text-[#171717]">
                  <ArrowLeft size={20} />
                </button>
                <h1 className="text-lg font-bold text-[#171717] m-0">Appointment Details</h1>
              </div>
              <div className="header-actions flex items-center gap-2 relative">
                <button
                  type="button"
                  className="flex items-center justify-center w-9 h-9 p-0 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={handleSaveFile}
                  aria-label="Export"
                >
                  <Upload className="h-4 w-4 text-[#2563EB]" />
                </button>

                <div className="relative">
                  <button
                    type="button"
                    className="flex items-center justify-center w-9 h-9 p-0 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={toggleDropdown}
                    aria-label="Share"
                  >
                    <Share2 className="h-4 w-4 text-[#2563EB]" />
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-40 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-lg py-1 z-[9999] text-sm text-[#334155]">
                      <button
                        className="block w-full text-left px-4 py-2 bg-white border-0 hover:bg-[#F8FAFC]"
                        onClick={handleEmailShare}
                      >
                        Share on Email
                      </button>
                      <button
                        className="block w-full text-left px-4 py-2 bg-white border-0 hover:bg-[#F8FAFC]"
                        onClick={handleSaveFile}
                      >
                        Save as File
                      </button>
                    </div>
                  )}
                </div>

                <button className="action-button flex items-center justify-center bg-transparent border-0 p-0 ml-1 text-[#171717]" onClick={onClose}>
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* No tabs needed since we only have Details */}

            {/* Content */}
            <div>
              {/* Basic Information */}
              <div className="bg-white px-4 overflow-hidden">
                <div className="pt-4">
                  <h3 className="text-base font-semibold text-[#171717] m-0 mb-4">Appointment Information</h3>
                </div>

                <div className="pb-4">
                  <div className="flex justify-between items-center py-3 border-b-[1px] border-solid border-x-0 border-t-0 border-[#F1F5F9] last:border-b-0">
                    <span className="text-[#64748B] text-sm font-normal">Mode</span>
                    <div className="flex items-center gap-1.5 px-2.5 py-0.5 bg-[#EFF6FF] rounded-full">
                      <img src={getModeIcon(appointmentDetails.mode)} alt={appointmentDetails.mode} className="w-4 h-4 object-contain" />
                      <span className="text-[#2563EB] text-[12px] font-semibold">{appointmentDetails.mode}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b-[1px] border-solid border-x-0 border-t-0 border-[#F1F5F9] last:border-b-0">
                    <span className="text-[#64748B] text-sm font-normal">Date</span>
                    <span className="text-[#171717] font-medium text-right max-w-[65%] break-words text-sm">
                      {appointmentDetails.date}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b-[1px] border-solid border-x-0 border-t-0 border-[#F1F5F9] last:border-b-0">
                    <span className="text-[#64748B] text-sm font-normal">Time</span>
                    <span className="text-[#171717] font-medium text-sm">
                      {appointmentDetails.timeSlot}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b-[1px] border-solid border-x-0 border-t-0 border-[#F1F5F9] last:border-b-0">
                    <span className="text-[#64748B] text-sm font-normal">Risk</span>
                    <div
                      className="px-2.5 py-0.5 flex items-center justify-center rounded-full text-[11px] font-semibold uppercase tracking-[0.5px] font-poppins"
                      style={{
                        backgroundColor: getRiskStatusBadge(appointmentDetails.riskStatusRaw || null).backgroundColor,
                        color: getRiskStatusBadge(appointmentDetails.riskStatusRaw || null).color
                      }}
                    >
                      {getRiskStatusBadge(appointmentDetails.riskStatusRaw || null).text}
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b-[1px] border-solid border-x-0 border-t-0 border-[#F1F5F9] last:border-b-0">
                    <span className="text-[#64748B] text-sm font-normal">Duration</span>
                    <span className="text-[#171717] font-medium text-sm">
                      {appointmentDetails.duration}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b-[1px] border-solid border-x-0 border-t-0 border-[#F1F5F9] last:border-b-0">
                    <span className="text-[#64748B] text-sm font-normal">Practitioner</span>
                    <span className="text-[#171717] font-medium text-right max-w-[65%] break-words text-sm">
                      {appointmentDetails.practitioner}
                    </span>
                  </div>
                </div>
              </div>

              {/* Member Information */}
              <div className="bg-white overflow-hidden mt-4">
                <h3 className="text-base font-semibold text-[#171717] m-0 px-4 pt-4 pb-2">Member Information</h3>
                <div className="px-4 pb-4">
                  <div className="flex justify-between items-center py-3 border-b-[1px] border-solid border-x-0 border-t-0 border-[#F1F5F9]">
                    <span className="text-[#64748B] text-sm font-normal">Name</span>
                    <span className="text-[#171717] font-medium text-sm">
                      {appointmentDetails.memberName}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b-[1px] border-solid border-x-0 border-t-0 border-[#F1F5F9]">
                    <span className="text-[#64748B] text-sm font-normal">Email</span>
                    <span className="text-[#171717] font-medium text-right max-w-[65%] break-words text-sm">
                      {appointmentDetails.memberEmail}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b-[1px] border-solid border-x-0 border-t-0 border-[#F1F5F9]">
                    <span className="text-[#64748B] text-sm font-normal">Phone</span>
                    <span className="text-[#171717] font-medium text-sm">
                      {appointmentDetails.memberPhone}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b-[1px] border-solid border-x-0 border-t-0 border-[#F1F5F9]">
                    <span className="text-[#64748B] text-sm font-normal">Gender</span>
                    <span className="text-[#171717] font-medium text-sm">
                      {appointmentDetails.memberGender}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b-[1px] border-solid border-x-0 border-t-0 border-[#F1F5F9] last:border-b-0">
                    <span className="text-[#64748B] text-sm font-normal">Groups</span>
                    <span className="text-[#171717] font-medium text-right max-w-[65%] break-words text-sm">
                      {appointmentDetails.memberGroups}
                    </span>
                  </div>
                </div>
              </div>


              {/* Session History Section */}
              <div className="mt-6 px-4">
                <SessionHistory
                  sessions={myAppointment}
                  onExport={handlePDFExport}
                  onSessionUpdated={onSessionUpdated}
                  appointmentId={appointmentDetails.appointmentId}
                />
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="font-poppins">
            {/* Header */}
            <div className="flex justify-between items-center px-[16px] py-[12px]">
                <div className="flex items-center gap-3">
                  <button className="flex items-center justify-center bg-transparent border-none cursor-pointer text-[#171717] p-1 rounded-full transition-colors hover:bg-gray-100" onClick={onBack}>
                    <ArrowLeft size={20} />
                  </button>
                  <h1 className="text-xl font-bold text-[#171717] font-poppins m-0">Appointment Details</h1>
                </div>
                <div className="flex items-center gap-2 relative">
                  <button
                    type="button"
                    className="flex items-center justify-center w-9 h-9 p-0 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={handleSaveFile}
                    title="Export"
                    aria-label="Export"
                  >
                    <Upload className="h-4 w-4 text-[#2563EB]" />
                  </button>

                  <div className="relative">
                    <button
                      type="button"
                      className="flex items-center justify-center w-9 h-9 p-0 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={toggleDropdown}
                      title="Share"
                      aria-label="Share"
                    >
                      <Share2 className="h-4 w-4 text-[#2563EB]" />
                    </button>

                    {isDropdownOpen && (
                      <div className="absolute right-0 mt-2 w-40 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-lg py-1 z-[9999] font-poppins text-sm text-[#334155]">
                        <button
                          className="block w-full text-left px-4 py-2 bg-white border-0 hover:bg-[#F8FAFC]"
                          onClick={handleEmailShare}
                        >
                          Share on Email
                        </button>
                        <button
                          className="block w-full text-left px-4 py-2 bg-white border-0 hover:bg-[#F8FAFC]"
                          onClick={handleSaveFile}
                        >
                          Save as File
                        </button>
                      </div>
                    )}
                  </div>

                  <button className="flex items-center justify-center bg-transparent border-none cursor-pointer text-[#171717] p-1 rounded-full transition-all hover:bg-gray-100 ml-1" onClick={onClose}>
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Content with separator after header */}
              <div className="w-full h-px bg-[#F1F5F9] mb-4"></div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 px-4 md:px-6 pb-4">
                {/* Left Section - Appointment Details and Practitioner Info */}
                <div className="flex flex-col gap-4">
                  {/* Appointment Details Section */}
                  <div className="bg-transparent p-0">
                    <h3 className="text-base font-semibold text-[#171717] font-poppins mb-2">Appointment Details</h3>
                    <div className="bg-white border-[1px] border-solid border-[#E2E8F0] p-[14px] rounded-2xl shadow-sm">
                      <div className="space-y-2.5">
                        <div className="flex justify-between items-center py-1 gap-3">
                          <span className="text-[14px] font-normal text-[#64748B]">Mode</span>
                          <div className="flex items-center gap-1.5 px-2.5 py-0.5 bg-[#EFF6FF] rounded-full">
                            <img src={getModeIcon(appointmentDetails.mode)} alt={appointmentDetails.mode} className="w-4 h-4 object-contain" />
                            <span className="text-[#2563EB] text-[12px] font-semibold">{appointmentDetails.mode}</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center py-1 gap-3">
                          <span className="text-[14px] font-normal text-[#64748B]">Status</span>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${appointmentDetails.statusColor}`}></div>
                            <span className="text-[#4B61FF] text-[14px] font-semibold">{appointmentDetails.status}</span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center py-1 gap-3">
                          <span className="text-[14px] font-normal text-[#64748B]">Date</span>
                          <span className="text-[14px] font-medium text-[#171717] text-right break-words">
                            {appointmentDetails.date}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-1 gap-3">
                          <span className="text-[14px] font-normal text-[#64748B]">Time</span>
                          <span className="text-[14px] font-medium text-[#171717] text-right break-words">
                            {appointmentDetails.timeSlot}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-1 gap-3">
                          <span className="text-[14px] font-normal text-[#64748B]">Risk</span>
                          <div 
                            className="px-2.5 py-0.5 flex items-center justify-center rounded-full text-[11px] font-semibold uppercase tracking-[0.5px] font-poppins"
                            style={{
                              backgroundColor: getRiskStatusBadge(appointmentDetails.riskStatusRaw || null).backgroundColor,
                              color: getRiskStatusBadge(appointmentDetails.riskStatusRaw || null).color
                            }}
                          >
                            {getRiskStatusBadge(appointmentDetails.riskStatusRaw || null).text}
                          </div>
                        </div>
                        <div className="flex justify-between items-center py-1 gap-3">
                          <span className="text-[14px] font-normal text-[#64748B]">Appointment Created By</span>
                          <span className="text-[14px] font-medium text-[#171717] text-right break-words">
                            {appointmentDetails.appointmentCreatedBy}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-1 gap-3">
                          <span className="text-[14px] font-normal text-[#64748B]">Duration</span>
                          <span className="text-[14px] font-medium text-[#171717] text-right break-words">
                            {appointmentDetails.duration}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>

                {/* Right Section - Basic Information and Status Logs/Timeline */}
                <div className="flex flex-col gap-4">
                  {/* Basic Information Section */}
                  <div className="bg-transparent p-0">
                    <h3 className="text-base font-semibold text-[#171717] font-poppins mb-2">Basic Information</h3>
                    <div className="bg-white border-[1px] border-solid border-[#E2E8F0] p-[14px] rounded-2xl shadow-sm">
                      <div className="space-y-2.5">
                        <div className="flex justify-between items-center py-1 gap-3">
                          <span className="text-[14px] font-normal text-[#64748B]">Patient Name</span>
                          <span className="text-[14px] font-medium text-[#171717] text-right break-words">
                            {appointmentDetails.memberName}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-1 gap-3">
                          <span className="text-[14px] font-normal text-[#64748B]">Contact Number</span>
                          <span className="text-[14px] font-medium text-[#171717] text-right break-words">
                            {appointmentDetails.memberPhone}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-1 gap-3">
                          <span className="text-[14px] font-normal text-[#64748B]">Email Address</span>
                          <span className="text-[14px] font-medium text-[#171717] text-right break-words">
                            {appointmentDetails.memberEmail}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-1 gap-3">
                          <span className="text-[14px] font-normal text-[#64748B]">Gender</span>
                          <span className="text-[14px] font-medium text-[#171717] text-right break-words">
                            {appointmentDetails.memberGender}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-1 gap-3">
                          <span className="text-[14px] font-normal text-[#64748B]">DOB</span>
                          <span className="text-[14px] font-medium text-[#171717] text-right break-words">
                            {appointmentDetails.memberDOB}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-1 gap-3">
                          <span className="text-[14px] font-normal text-[#64748B]">Groups</span>
                          <span className="text-[14px] font-medium text-[#171717] text-right break-words">
                            {appointmentDetails.memberGroups}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              {/* Session History Section */}
              <div className="mt-2 px-4 md:px-6 pb-6">
                <SessionHistory sessions={myAppointment} onExport={onExport} onSessionUpdated={onSessionUpdated} />
              </div>
          </div>
        </>
      )}
    </>
  );
};

// PropTypes validation
AppointmentDetails.propTypes = {
  onBack: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onShare: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  onSessionUpdated: PropTypes.func
};

// Component displayName for debugging
AppointmentDetails.displayName = 'AppointmentDetails';

export default AppointmentDetails;